<template>
  <div class="p-4">
    <p class="mb-4">{{ $t('move_assignments_pick_target_hint') }}</p>

    <b-field :label="$t('activity')" :type="!selectedActivity ? 'is-warning' : ''">
      <b-autocomplete
        v-model="activityQuery"
        :data="filteredActivities"
        field="display_name"
        :placeholder="$t('select_activity')"
        :loading="isLoading"
        :open-on-focus="true"
        :clearable="true"
        @select="onActivitySelected"
        rounded
      >
        <template slot-scope="props">
          <div class="flex flex-col">
            <span class="font-bold">{{ props.option.name }}</span>
            <span class="text-xs text-grey-darker" v-if="props.option.planning_code">
              {{ props.option.planning_code }}
            </span>
          </div>
        </template>
        <template #empty>{{ $t('no_results') }}</template>
      </b-autocomplete>
    </b-field>

    <b-field
      v-if="selectedActivity && subactivities.length > 0"
      :label="$t('subactivity') + ' (' + $t('optional') + ')'"
    >
      <b-autocomplete
        v-model="subactivityQuery"
        :data="filteredSubactivities"
        field="display_name"
        :placeholder="$t('select_subactivity')"
        :loading="isLoadingSub"
        :open-on-focus="true"
        :clearable="true"
        @select="onSubactivitySelected"
        rounded
      >
        <template slot-scope="props">
          <div class="flex flex-col">
            <span class="font-bold">{{ props.option.name }}</span>
            <span class="text-xs text-grey-darker" v-if="props.option.planning_code">
              {{ props.option.planning_code }}
            </span>
          </div>
        </template>
        <template #empty>{{ $t('no_results') }}</template>
      </b-autocomplete>
    </b-field>

    <div class="mt-4 p-3 bg-blue-lightest rounded" v-if="selectedTarget">
      <p class="text-sm">
        <b-icon pack="fas" icon="info-circle" size="is-small" type="is-info"></b-icon>
        {{ $t('move_assignments_target_selected') }}:
        <strong>{{ selectedTarget.name }}</strong>
        <span v-if="selectedTarget.planning_code" class="text-grey-darker">
          ({{ selectedTarget.planning_code }})
        </span>
      </p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'move-assignments-target',

  props: {
    projectId: { type: Number, required: true },
    action: { type: Object, default: null },
  },

  data: () => ({
    activities: [],
    subactivities: [],
    selectedActivity: null,
    selectedSubactivity: null,
    activityQuery: '',
    subactivityQuery: '',
    isLoading: false,
    isLoadingSub: false,
  }),

  computed: {
    filteredActivities() {
      const q = (this.activityQuery || '').toLowerCase().trim();
      const list = this.activities.map(a => ({
        ...a,
        display_name: a.planning_code ? `${a.name} (${a.planning_code})` : a.name,
      }));
      if (!q) return list;
      return list.filter(a =>
        (a.name || '').toLowerCase().includes(q) ||
        (a.planning_code || '').toLowerCase().includes(q)
      );
    },

    filteredSubactivities() {
      const q = (this.subactivityQuery || '').toLowerCase().trim();
      const list = this.subactivities.map(a => ({
        ...a,
        display_name: a.planning_code ? `${a.name} (${a.planning_code})` : a.name,
      }));
      if (!q) return list;
      return list.filter(a =>
        (a.name || '').toLowerCase().includes(q) ||
        (a.planning_code || '').toLowerCase().includes(q)
      );
    },

    selectedTarget() {
      return this.selectedSubactivity || this.selectedActivity;
    },
  },

  watch: {
    selectedTarget(target) {
      const payload = target ? [{ id: target.id, name: target.name, planning_code: target.planning_code }] : [];
      this.$emit('selected-linked-entities', payload);
    },
  },

  created() {
    this.loadActivities();
  },

  methods: {
    async loadActivities() {
      this.isLoading = true;
      try {
        const { data } = await axios.get('/api/v2/activities', {
          params: { p: this.projectId, light: 1, per_page: 999 },
        });
        const all = (data && data.activities) || [];
        this.activities = all.filter(a => !a.parent_id);
      } catch (e) {
        this.$notify.error('error_loading_activities');
      } finally {
        this.isLoading = false;
      }
    },

    async onActivitySelected(option) {
      this.selectedActivity = option || null;
      this.selectedSubactivity = null;
      this.subactivityQuery = '';
      this.subactivities = [];
      if (!option) return;

      this.isLoadingSub = true;
      try {
        const { data } = await axios.get(`/api/v2/activity/${option.id}/subactivities`);
        this.subactivities = (data && data.subactivities) || [];
      } catch (e) {
        this.subactivities = [];
      } finally {
        this.isLoadingSub = false;
      }
    },

    onSubactivitySelected(option) {
      this.selectedSubactivity = option || null;
    },
  },
};
</script>
